"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { Button, Modal, ModalHeader, ModalBody, FormGroup, Label, Input, Spinner, FormFeedback } from "reactstrap"
import { ExhibitorCarService } from "../../../Service/ExhibitorCarService"
import type { ExhibitorCar, ExhibitorOption, ParticipationOption, UserRoles } from "./types"
import { toast } from "react-toastify"
import Swal from "sweetalert2"
import AuthService from "../../../Service/AuthService"

interface Props {
  isOpen: boolean
  toggle: () => void
  onSuccess: () => void
  initialData: ExhibitorCar | null
}

const ExhibitorCarModal = ({ isOpen, toggle, onSuccess, initialData }: Props) => {
  const [form, setForm] = useState<Omit<ExhibitorCar, "id" | "created_at" | "created_by">>({
    exhibitor: 0,
    participation: 0,
    plate_number: "",
    driver_name: "",
    driver_phone: "",
    status: true,
  })

  const [exhibitors, setExhibitors] = useState<ExhibitorOption[]>([])
  const [participations, setParticipations] = useState<ParticipationOption[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingParticipations, setLoadingParticipations] = useState(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [userRoles, setUserRoles] = useState<UserRoles>({
    is_registrar: false,
    is_staff: false,
    is_verifier: false,
    is_admin: false,
    is_superuser: false,
  })

  useEffect(() => {
    const decodedToken = AuthService.getDecodedToken()
    if (decodedToken) {
      setUserRoles({
        is_registrar: decodedToken.is_registrar || false,
        is_staff: decodedToken.is_staff || false,
        is_verifier: decodedToken.is_verifier || false,
        is_admin: decodedToken.is_admin || false,
        is_superuser: decodedToken.is_superuser || false,
      })
    }
  }, [])

  // Handle initial data and form reset
  useEffect(() => {
    if (initialData) {
      setForm({
        exhibitor: initialData.exhibitor,
        participation: initialData.participation,
        plate_number: initialData.plate_number,
        driver_name: initialData.driver_name,
        driver_phone: initialData.driver_phone,
        status: initialData.status,
      })
    } else {
      // Reset form for new entry
      setForm({
        exhibitor: 0,
        participation: 0,
        plate_number: "",
        driver_name: "",
        driver_phone: "",
        status: true,
      })
      setParticipations([])
    }
  }, [initialData])

  // Fetch exhibitors when modal opens (only for non-staff users)
  useEffect(() => {
    if (isOpen && !isStaffOnly) {
      fetchExhibitors()
    }
  }, [isOpen])

  // Fetch participations when form.exhibitor changes (only for non-staff users)
  useEffect(() => {
    if (form.exhibitor > 0 && !isStaffOnly) {
      fetchParticipations(form.exhibitor)
    } else {
      setParticipations([])
    }
  }, [form.exhibitor])

  const fetchExhibitors = async () => {
    try {
      const exhibitorsData = await ExhibitorCarService.getExhibitors()
      setExhibitors(exhibitorsData)
    } catch (error) {
      console.error("Failed to fetch exhibitors:", error)
      toast.error("Failed to load exhibitors")
    }
  }

  const fetchParticipations = async (exhibitorId: number) => {
    try {
      setLoadingParticipations(true)
      const participationsData = await ExhibitorCarService.getParticipations(exhibitorId)
      setParticipations(participationsData)
    } catch (error) {
      console.error("Failed to fetch participations:", error)
      toast.error("Failed to load participations")
      setParticipations([])
    } finally {
      setLoadingParticipations(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target

    if (name === "exhibitor") {
      const exhibitorId = Number.parseInt(value) || 0
      setForm({
        ...form,
        [name]: exhibitorId,
        participation: 0, // Reset participation when exhibitor changes
      })
    } else {
      setForm({
        ...form,
        [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
      })
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }))
    }
  }

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    // Plate number is always required
    if (!form.plate_number.trim()) {
      newErrors.plate_number = "Plate number is required"
    }

    // For non-staff users, validate all fields
    if (!isStaffOnly) {
      if (!form.driver_name.trim()) {
        newErrors.driver_name = "Driver name is required"
      }
      if (!form.driver_phone.trim()) {
        newErrors.driver_phone = "Driver phone is required"
      }
      if (form.exhibitor === 0) {
        newErrors.exhibitor = "Exhibitor is required"
      }
      if (form.participation === 0) {
        newErrors.participation = "Participation is required"
      }
    }

    return newErrors
  }

  const handleSubmit = async () => {
    const validationErrors = validateForm()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    try {
      setLoading(true)

      // For staff users creating new cars, only send plate_number
      let submitData = form
      if (isStaffOnly && !initialData) {
        submitData = {
          plate_number: form.plate_number,
          // These will be ignored but needed for type compatibility
          status: true, // Default to active
          exhibitor: 0,
          participation: 0,
          driver_name: "",
          driver_phone: "",
        }
      }

      if (initialData) {
        await ExhibitorCarService.update(initialData.id, submitData)
        await Swal.fire("Updated!", "Car updated successfully.", "success")
      } else {
        await ExhibitorCarService.create(submitData)
        await Swal.fire("Created!", "Car registered successfully.", "success")
      }
      onSuccess()
      toggle()
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || "Something went wrong. Please try again."
      await Swal.fire("Error", errorMessage, "error")
    } finally {
      setLoading(false)
    }
  }

  const isStaffOnly = userRoles.is_staff && !userRoles.is_admin && !userRoles.is_superuser

  return (
    <Modal isOpen={isOpen} toggle={toggle} size={isStaffOnly && !initialData ? "sm" : "lg"}>
      <ModalHeader toggle={toggle}>{initialData ? "Edit" : "Register"} Car</ModalHeader>
      <ModalBody>
        {/* Show full form for non-staff users or when editing */}
        {!isStaffOnly || initialData ? (
          <>
            <div className="row">
              <FormGroup className="col-md-6">
                <Label for="exhibitor">Exhibitor *</Label>
                <Input
                  type="select"
                  name="exhibitor"
                  value={form.exhibitor}
                  onChange={handleChange}
                  invalid={!!errors.exhibitor}
                  disabled={isStaffOnly}
                >
                  <option value="">-- Select Exhibitor --</option>
                  {exhibitors.map((exhibitor) => (
                    <option key={exhibitor.id} value={exhibitor.id}>
                      {exhibitor.name}
                    </option>
                  ))}
                </Input>
                <FormFeedback>{errors.exhibitor}</FormFeedback>
                {isStaffOnly && <small className="text-muted">Auto-selected based on your role</small>}
              </FormGroup>

              <FormGroup className="col-md-6">
                <Label for="participation">Participation *</Label>
                <Input
                  type="select"
                  name="participation"
                  value={form.participation}
                  onChange={handleChange}
                  invalid={!!errors.participation}
                  disabled={isStaffOnly || form.exhibitor === 0 || loadingParticipations}
                >
                  <option value="">{loadingParticipations ? "Loading..." : "-- Select Participation --"}</option>
                  {participations.map((participation) => (
                    <option key={participation.id} value={participation.id}>
                      {participation.exhibition_title}
                      {participation.stand_category && ` - ${participation.stand_category}`}
                    </option>
                  ))}
                </Input>
                <FormFeedback>{errors.participation}</FormFeedback>
                {loadingParticipations && (
                  <small className="text-muted">
                    <Spinner size="sm" className="me-1" />
                    Loading participations...
                  </small>
                )}
                {isStaffOnly && <small className="text-muted">Auto-selected based on your exhibitor</small>}
              </FormGroup>
            </div>

            <div className="row">
              <FormGroup className="col-md-6">
                <Label for="plate_number">Plate Number *</Label>
                <Input
                  name="plate_number"
                  value={form.plate_number}
                  onChange={handleChange}
                  invalid={!!errors.plate_number}
                  placeholder="Enter plate number"
                />
                <FormFeedback>{errors.plate_number}</FormFeedback>
              </FormGroup>

              <FormGroup check className="form-switch col-md-6 d-flex align-items-center justify-content-end">
                <Input type="checkbox" name="status" checked={form.status} onChange={handleChange} />
                <Label check>Active Status</Label>
              </FormGroup>
            </div>

            <div className="row">
              <FormGroup className="col-md-6">
                <Label for="driver_name">Driver Name *</Label>
                <Input
                  name="driver_name"
                  value={form.driver_name}
                  onChange={handleChange}
                  invalid={!!errors.driver_name}
                  placeholder="Enter driver name"
                  disabled={isStaffOnly}
                />
                <FormFeedback>{errors.driver_name}</FormFeedback>
                {isStaffOnly && <small className="text-muted">Auto-filled from your profile</small>}
              </FormGroup>

              <FormGroup className="col-md-6">
                <Label for="driver_phone">Driver Phone *</Label>
                <Input
                  name="driver_phone"
                  value={form.driver_phone}
                  onChange={handleChange}
                  invalid={!!errors.driver_phone}
                  placeholder="Enter driver phone"
                  disabled={isStaffOnly}
                />
                <FormFeedback>{errors.driver_phone}</FormFeedback>
                {isStaffOnly && <small className="text-muted">Auto-filled from your profile</small>}
              </FormGroup>
            </div>
          </>
        ) : (
          /* Ultra-simplified form for staff users creating new cars - ONLY plate number */
          <FormGroup>
            <Label for="plate_number" className="h5 mb-3">
              Plate Number *
            </Label>
            <Input
              name="plate_number"
              value={form.plate_number}
              onChange={handleChange}
              invalid={!!errors.plate_number}
              placeholder="Enter plate number"
              className="form-control-lg text-center"
              style={{ fontSize: "1.2rem", padding: "15px" }}
            />
            <FormFeedback>{errors.plate_number}</FormFeedback>
          </FormGroup>
        )}

        <div className="text-end mt-4">
          <Button color="secondary" onClick={toggle} className="me-2">
            Cancel
          </Button>
          <Button color="primary" onClick={handleSubmit} disabled={loading}>
            {loading ? <Spinner size="sm" /> : initialData ? "Update" : "Register"}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default ExhibitorCarModal
